// Builder-only reducer for the pipeline editor. Trimmed from the POC's store —
// the reporting library owns navigation/persistence, so workspace/chooser/save
// state is gone. What remains: the op-chain plus transient editing state
// (which step is inspected, where the op menu is anchored).
import { ReportView } from '../components/ViewSwitcher'
import { DEFAULT_FRAME } from '../frames'
import { FrameId, Op, PickableOp } from '../pipeline-types'
import { ViewConfig } from '../view-config'

export interface State {
  frame: FrameId // the base table the chain runs against
  ops: Op[]
  view: ReportView | null // explicit "Show as" override; null → shape default
  threshold: ViewConfig['threshold'] | null

  // Transient editing state.
  cursor: number | null // inspected step (null = end of chain)
  insertAt: number | null // anchor for the op menu/config (null = closed)
  menuMode: 'menu' | 'config' | null
  pendingOp: PickableOp | null
}

export function initialState(
  ops: Op[] = [],
  view: ReportView | null = null,
  frame: FrameId = DEFAULT_FRAME
): State {
  return {
    frame,
    ops,
    view,
    threshold: null,
    cursor: null,
    insertAt: null,
    menuMode: null,
    pendingOp: null,
  }
}

// Clear transient editing state (cursor + menu/config), never the frame itself.
const CLOSED = { insertAt: null, menuMode: null, pendingOp: null } as const

export type Action =
  | { t: 'SET_FRAME'; frame: FrameId }
  | { t: 'SET_VIEW'; view: ReportView }
  | { t: 'REMOVE_OP'; i: number }
  | { t: 'SET_CURSOR'; cursor: number | null }
  | { t: 'OPEN_MENU'; at: number }
  | { t: 'PICK_OP'; op: PickableOp }
  | { t: 'ADD_OP'; op: Op }
  | { t: 'CANCEL_OP' }
  | { t: 'SET_THRESHOLD'; threshold: ViewConfig['threshold'] }
  | { t: 'CLEAR_THRESHOLD' }

export function reducer(s: State, a: Action): State {
  switch (a.t) {
    case 'SET_FRAME':
      // A frame swap changes the available columns, so the existing chain no
      // longer applies — start fresh from the new base table.
      if (a.frame === s.frame) return s
      return { ...initialState([], s.view, a.frame) }
    case 'SET_VIEW':
      return { ...s, view: a.view }
    case 'REMOVE_OP':
      return {
        ...s,
        ops: s.ops.filter((_, i) => i !== a.i),
        cursor: null,
        ...CLOSED,
      }
    case 'SET_CURSOR':
      return { ...s, cursor: a.cursor, ...CLOSED }
    case 'OPEN_MENU': {
      // Clicking the same anchor again toggles the menu shut.
      if (s.menuMode === 'menu' && s.insertAt === a.at)
        return { ...s, ...CLOSED }
      return { ...s, insertAt: a.at, menuMode: 'menu', pendingOp: null }
    }
    case 'PICK_OP':
      return { ...s, menuMode: 'config', pendingOp: a.op }
    case 'ADD_OP': {
      const at = s.insertAt == null ? s.ops.length : s.insertAt
      const ops = [...s.ops.slice(0, at), a.op, ...s.ops.slice(at)]
      return { ...s, ops, cursor: null, ...CLOSED }
    }
    case 'CANCEL_OP':
      return { ...s, ...CLOSED }
    case 'SET_THRESHOLD':
      return { ...s, threshold: a.threshold }
    case 'CLEAR_THRESHOLD':
      return { ...s, threshold: null }
  }
}
